import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Engagement-wide search-index health surface (T-0120 / PR #142).
 *
 *   GET  /api/v1/engagements/:engagement_id/search-index/health
 *     → {
 *         engagement_id,
 *         document_count,
 *         chunk_count,
 *         last_indexed_at,
 *         stale_documents: [{ document_id, title, last_indexed_at, last_modified_at }],
 *         health_status: 'fresh' | 'stale' | 'indexing',
 *       }
 *
 *   POST /api/v1/engagements/:engagement_id/search-index/reindex-all
 *     → 202 {
 *         engagement_id,
 *         documents_enqueued,
 *         documents_skipped,
 *         correlation_id,
 *       }
 *
 * Both surfaces are engagement-scoped per ADR-013 (the per-engagement
 * namespace is the index isolation boundary). The reindex-all path
 * fans out one `document.parsed` envelope per document with a parsed
 * ExtractionRun; the T-0043 indexing worker handles each envelope
 * idempotently via upsert-by-search_doc_id, so replays don't duplicate
 * chunks. The route honors `Idempotency-Key` per cross-cutting §2.
 */

export const SEARCH_INDEX_HEALTH_STATUSES = ['fresh', 'stale', 'indexing'] as const;
export type SearchIndexHealthStatus = (typeof SEARCH_INDEX_HEALTH_STATUSES)[number];

export const StaleDocumentSchema = z.object({
  document_id: z.string().uuid(),
  title: z.string(),
  last_indexed_at: z.string().nullable(),
  last_modified_at: z.string().nullable(),
});
export type StaleDocument = z.infer<typeof StaleDocumentSchema>;

export const SearchIndexHealthSchema = z.object({
  engagement_id: z.string().uuid(),
  /** The engagement's live (not deleted) documents, indexed or not. */
  document_count: z.number().int().nonnegative(),
  /** Indexed passages of those documents. */
  chunk_count: z.number().int().nonnegative(),
  last_indexed_at: z.string().nullable(),
  stale_documents: z.array(StaleDocumentSchema),
  health_status: z.enum(SEARCH_INDEX_HEALTH_STATUSES),
});
export type SearchIndexHealth = z.infer<typeof SearchIndexHealthSchema>;

export function getSearchIndexHealth(engagementId: string): Promise<SearchIndexHealth> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/search-index/health`,
    SearchIndexHealthSchema,
  );
}

export function useSearchIndexHealthQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['search-index', engagementId, 'health'] as const,
    queryFn: () => getSearchIndexHealth(engagementId!),
    enabled: Boolean(engagementId),
    staleTime: 15_000,
  });
}

export const ReindexAllResultSchema = z.object({
  engagement_id: z.string().uuid(),
  documents_enqueued: z.number().int().nonnegative(),
  documents_skipped: z.number().int().nonnegative(),
  correlation_id: z.string(),
});
export type ReindexAllResult = z.infer<typeof ReindexAllResultSchema>;

export function reindexAllSearchIndex(
  engagementId: string,
  idempotencyKey?: string,
): Promise<ReindexAllResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/search-index/reindex-all`,
    ReindexAllResultSchema,
    {
      method: 'POST',
      body: {},
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useReindexAllSearchIndexMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ idempotencyKey }: { idempotencyKey?: string } = {}) =>
      reindexAllSearchIndex(engagementId!, idempotencyKey),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['search-index', engagementId] });
      }
    },
  });
}
